/**
 * Lobby de quem está autenticado.
 *
 * Diferente do lobby da demonstração em uma coisa que muda tudo: aqui o
 * adversário é uma pessoa. E a fila procura pessoa, não preço — o valor é
 * combinado depois, com o adversário na frente. Por isso o que se escolhe
 * aqui é uma intenção, não um compromisso: nada sai do saldo enquanto os
 * dois não fecharem o valor na tela seguinte.
 */
import { useEffect, useRef, useState } from 'react';

import { PresencaJogoBadge, PresencaTotal, usePresenca } from '../components/Presenca.tsx';
import { MesaCard } from '../components/MesaCard.tsx';
import type { GameType } from '../lib/adapter.ts';
import {
  type CarteiraVirtual,
  type EstadoAposta,
  carregarCarteiraVirtual,
  entrarNaFila,
  sairDaFila,
  situacaoNaFila,
} from '../lib/conta.ts';
import { STAKES, payoutCents, rakeCents } from '../lib/demo.ts';
import { GAME_LABEL, money } from '../lib/format.ts';

const JOGOS: readonly GameType[] = ['TIC_TAC_TOE', 'CHECKERS', 'CHESS'];

const DESCRICAO: Record<GameType, string> = {
  TIC_TAC_TOE: 'Rápido. Bom para conhecer a mesa.',
  CHECKERS: 'Damas brasileiras: captura obrigatória e lei da maioria.',
  CHESS: 'Regras completas, relógio com incremento.',
};

type Situacao =
  | { readonly tipo: 'parado' }
  | { readonly tipo: 'procurando' }
  | { readonly tipo: 'na-fila' }
  | { readonly tipo: 'pareado' }
  | { readonly tipo: 'erro'; readonly mensagem: string };

export function LobbyReal({
  userId,
  token,
  stakeMode,
  saldoRealCents,
  onPartida,
}: {
  userId: string;
  token: string;
  /**
   * Treino ou valendo. Vem de fora porque o salão e esta tela precisam
   * concordar: abrir mesa num modo e listá-las no outro deixaria a pessoa
   * esperando numa sala em que ninguém a vê.
   */
  stakeMode: 'REAL' | 'VIRTUAL';
  /** Saldo real, para a conta do pote quando o modo é valendo. */
  saldoRealCents: number;
  onPartida: (matchId: string, gameType: GameType, aposta: EstadoAposta | null) => void;
}) {
  const [jogo, setJogo] = useState<GameType>('TIC_TAC_TOE');
  // Nulo é escolha legítima, não ausência de escolha: significa "decido na
  // mesa, com o adversário na frente".
  const [sugestao, setSugestao] = useState<number | null>(null);
  const [carteira, setCarteira] = useState<CarteiraVirtual | null>(null);
  const [situacao, setSituacao] = useState<Situacao>({ tipo: 'parado' });
  const presenca = usePresenca(stakeMode);

  // `onPartida` e `jogo` mudam de identidade a cada render de quem nos
  // renderiza. Se entrassem nas dependências do intervalo, todo render
  // dentro da janela de três segundos zeraria o relógio antes de ele
  // disparar — e a consulta que descobre o pareamento nunca aconteceria.
  const aoParear = useRef(onPartida);
  const jogoAtual = useRef(jogo);
  aoParear.current = onPartida;
  jogoAtual.current = jogo;

  // Consulta que falha não pode virar espera infinita. Sem isto, qualquer
  // erro do serviço deixa o jogador olhando "Na fila" para sempre, sem uma
  // única pista do que houve — nem para ele, nem para quem for depurar.
  const falhasSeguidas = useRef(0);

  useEffect(() => {
    void carregarCarteiraVirtual(userId).then(setCarteira);
  }, [userId, situacao.tipo]);

  // Na fila, pergunta onde está: o pareamento acontece quando o outro lado
  // chega, e quem entrou primeiro não recebe aviso.
  //
  // A pergunta é "onde eu estou", não "me enfileira de novo". Reenfileirar
  // devolve ALREADY_IN_MATCH a quem acabou de ser pareado — recusa no exato
  // instante em que devia chegar a boa notícia, e quem esperava ficava preso
  // nesta tela enquanto o adversário já estava na mesa.
  useEffect(() => {
    if (situacao.tipo !== 'na-fila') return;
    const timer = setInterval(() => {
      void situacaoNaFila(token).then((resultado) => {
        if (resultado.estado === 'pareado') {
          setSituacao({ tipo: 'pareado' });
          // O jogo vem da partida, não da seleção local: quem manda é o
          // servidor, e abrir o tabuleiro errado seria pior que esperar.
          aoParear.current(
            resultado.matchId,
            resultado.aposta?.gameType ?? jogoAtual.current,
            resultado.aposta,
          );
          return;
        }
        // Saiu da fila sem partida: prazo da fila venceu, ou a mesa foi
        // desfeita. Voltar ao início é melhor que fingir que ainda espera.
        if (resultado.estado === 'parado') {
          falhasSeguidas.current = 0;
          setSituacao({ tipo: 'parado' });
          return;
        }
        if (resultado.estado === 'na-fila') {
          falhasSeguidas.current = 0;
          return;
        }

        // Três tentativas seguidas sem resposta: uma falha isolada é
        // normal numa rede, três seguidas são um defeito, e o jogador
        // precisa saber que não adianta continuar esperando.
        falhasSeguidas.current += 1;
        if (falhasSeguidas.current >= 3) {
          setSituacao({
            tipo: 'erro',
            mensagem:
              'O serviço de partidas parou de responder enquanto você esperava. ' +
              'Você não perdeu saldo. Tente procurar de novo.',
          });
        }
      });
    }, 3_000);
    return () => clearInterval(timer);
  }, [situacao.tipo, token]);

  async function procurar(): Promise<void> {
    setSituacao({ tipo: 'procurando' });
    falhasSeguidas.current = 0;
    const resultado = await entrarNaFila(token, jogo, sugestao, stakeMode);

    if (resultado.estado === 'pareado') {
      setSituacao({ tipo: 'pareado' });
      onPartida(resultado.matchId, resultado.aposta?.gameType ?? jogo, resultado.aposta);
      return;
    }
    if (resultado.estado === 'na-fila') {
      setSituacao({ tipo: 'na-fila' });
      return;
    }
    setSituacao({
      tipo: 'erro',
      mensagem:
        resultado.estado === 'recusado'
          ? resultado.mensagem
          : 'O serviço de partidas não respondeu. Tente de novo.',
    });
  }

  async function cancelar(): Promise<void> {
    await sairDaFila(token);
    setSituacao({ tipo: 'parado' });
  }

  // No modo valendo o saldo que importa é o real; a carteira virtual
  // continua sendo lida porque é ela que vale no treino.
  const saldo = stakeMode === 'REAL' ? saldoRealCents : (carteira?.disponivelCents ?? null);
  const semSaldo = saldo !== null && saldo < (STAKES[0]?.cents ?? 100);
  const ocupado = situacao.tipo === 'procurando' || situacao.tipo === 'na-fila';

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 grid gap-6 lg:grid-cols-[1fr_20rem] items-start content-start grow">
      <section className="grid gap-4 content-start">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
              Abrir sua mesa
            </h1>
            {/* O modo não é escolhido aqui — vem do salão. Dizê-lo evita a
                dúvida de quem chegou por engano na sala errada e só
                descobriria ao ver o valor sair do saldo. */}
            <p className="text-sm text-muted-foreground">
              {stakeMode === 'REAL'
                ? 'Valendo dinheiro de verdade'
                : 'Treino, com saldo virtual'}
            </p>
          </div>
          <PresencaTotal presenca={presenca} />
        </div>

        <ul className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Jogo">
          {JOGOS.map((tipo) => (
            <li key={tipo}>
              <MesaCard
                gameType={tipo}
                titulo={GAME_LABEL[tipo] ?? tipo}
                descricao={DESCRICAO[tipo] ?? ''}
                selecionado={jogo === tipo}
                desabilitado={ocupado}
                onSelecionar={() => setJogo(tipo)}
                rodape={<PresencaJogoBadge presenca={presenca} gameType={tipo} />}
              />
            </li>
          ))}
        </ul>

        <h2 className="mt-2 rotulo">Valor que você pretende propor</h2>
        <p className="text-sm text-muted-foreground -mt-2">
          Opcional. A fila procura adversário para o jogo, não para o valor — vocês fecham o preço
          depois de se encontrarem.
        </p>

        <ul className="flex flex-wrap gap-2" role="radiogroup" aria-label="Valor que pretende propor">
          <li>
            <button
              type="button"
              role="radio"
              aria-checked={sugestao === null}
              onClick={() => setSugestao(null)}
              disabled={ocupado}
              className={['btn', sugestao === null ? 'btn-primary' : 'btn-ghost'].join(' ')}
            >
              Decidir na mesa
            </button>
          </li>
          {STAKES.map((stake) => (
            <li key={stake.cents}>
              <button
                type="button"
                role="radio"
                aria-checked={sugestao === stake.cents}
                onClick={() => setSugestao(stake.cents)}
                disabled={ocupado || (saldo !== null && stake.cents > saldo)}
                className={['btn', sugestao === stake.cents ? 'btn-primary' : 'btn-ghost'].join(' ')}
              >
                {stake.rotulo}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <aside className="card card-destaque p-5 grid gap-4 content-start">
        <h2 className="rotulo">Como funciona</h2>

        <ol className="grid gap-2 text-sm text-muted-foreground">
          <li>1. A fila encontra alguém para {GAME_LABEL[jogo]}.</li>
          <li>2. Vocês combinam o valor: um propõe, o outro aceita.</li>
          <li>3. No aceite, a entrada dos dois é travada ao mesmo tempo.</li>
        </ol>

        {sugestao !== null ? (
          <>
            <div className="border-t pt-3">
              <p className="rotulo">Se fecharem em {money(sugestao)}</p>
            </div>
            <dl className="grid gap-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Pote</dt>
                <dd className="money">{money(sugestao * 2)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Taxa da plataforma (5%)</dt>
                <dd className="money">−{money(rakeCents(sugestao))}</dd>
              </div>
              <div className="flex justify-between gap-4 border-t pt-2 font-semibold">
                <dt>Prêmio ao vencedor</dt>
                <dd className="money">{money(payoutCents(sugestao))}</dd>
              </div>
            </dl>
          </>
        ) : null}

        <p className="text-xs text-muted-foreground">
          Nada sai do saldo até os dois concordarem com o valor. Empate devolve a entrada integral,
          sem taxa.
        </p>

        {situacao.tipo === 'na-fila' ? (
          <>
            <p className="text-sm font-medium" aria-live="polite" style={{ color: 'var(--primary)' }}>
              Sua mesa de {GAME_LABEL[jogo]} está aberta no salão.
            </p>
            <p className="text-xs text-muted-foreground">
              Quem chegar depois vê sua mesa e senta. Dá para fechar a página: ela continua
              aberta.
            </p>
            <button type="button" className="btn btn-ghost" onClick={() => void cancelar()}>
              Fechar minha mesa
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={semSaldo || ocupado}
            onClick={() => void procurar()}
          >
            {situacao.tipo === 'procurando'
              ? 'Abrindo…'
              : semSaldo
                ? 'Saldo insuficiente'
                : 'Abrir mesa e esperar'}
          </button>
        )}

        {situacao.tipo === 'erro' ? (
          <p role="alert" className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
            {situacao.mensagem}
          </p>
        ) : null}
      </aside>
    </main>
  );
}
